import {
  type FlowIdentifier,
  IdxStatus,
  type IdxTransaction,
  isRedirectUri,
  OktaAuth,
  type Tokens,
} from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import { Spinner } from '@pcln/horizon'
import { useRouter } from 'next/router'
import { Box, ButtonChip, Container, Flex, Link, Text } from 'pcln-design-system'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { getRedirectUrlOnSignInComplete } from '@/components/Okta/utils/util'
import {
  clearSamlFlowTimer,
  getSamlFlowCorrelationId,
  getSamlFlowElapsedMs,
  startSamlFlowTimer,
} from '@/components/Okta/utils/webviewFlowTiming'
import analytics from '@/shared-utils/analytics'
import { getFromStorage, removeFromStorage, setToStorage } from '@/shared-utils/storage-helpers'

import BackButton from './BackButton'
import CanceledPage from './CanceledPage'
import ErrorPage from './ErrorPage'
import FlowPage from './FlowPage'
import { fireLoginSuccessEvent, firePageViewEvent } from './ga4'
import LoginCallback from './LoginCallback'
import TerminalPage from './TerminalPage'
import type { AuthTabType, InitialTransaction } from './utils/types'

const WebviewContainer = styled(Flex)`
  width: 100%;
  height: 100%;
  background-color: white;
  position: fixed;
  z-index: 4;
  top: 0px;
`

const oktaAuth = ({
  clientId,
  issuer,
  callbackUrl,
  clientScope,
}: {
  clientScope: string
  clientId: string
  issuer: string
  callbackUrl: string
}) => {
  return new OktaAuth({
    clientId,
    issuer,
    redirectUri: callbackUrl,
    scopes: ['openid', 'profile', 'email', 'offline_access', clientScope],
  })
}

const restoreOriginalUri = () => {
  window.location.href = '/'
}

const AUTH_TABS = [
  {
    autobotID: 'AUTH_TAB_SIGN_IN',
    name: 'SIGN IN',
    tabText: 'Sign In',
    id: 'authenticate',
    label: 'Sign In to your account',
    query: { flow: 'authenticate' },
  },
  {
    autobotID: 'AUTH_TAB_CREATE_ACCOUNT',
    name: 'CREATE ACCOUNT',
    tabText: 'Create Account',
    id: 'register',
    label: 'Create an account',
    query: { flow: 'register' },
  },
] as const

function logSignInSuccess(redirectUrl: string, activeFlow: FlowIdentifier) {
  analytics.log({
    message: `Standalone - login complete event received`,
  })
  const signInMethod = getFromStorage({ storageType: 'session', key: 'standalone_signin_with' })
  if (signInMethod) {
    removeFromStorage({ storageType: 'session', key: 'standalone_signin_with' })
    fireLoginSuccessEvent(signInMethod, redirectUrl, activeFlow)
  }
}

function Okta({
  redirectUrl,
  initialTransaction = null,
  clientId,
  issuer,
  callbackUrl,
  clientScope,
}: InitialTransaction) {
  const [transaction, setTransaction] = useState<IdxTransaction | null>(initialTransaction)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showSpinner, setShowSpinner] = useState(false)
  const [isWebview, setIsWebview] = useState(false)
  const [slideInRight, setSlideInRight] = useState(true)
  const [componentToShow, setComponentToShow] = useState<ReactNode>()
  const [buttonsDisabled, setButtonsDisabled] = useState(false)

  const oktaClient = useMemo(
    () => oktaAuth({ clientScope, clientId, issuer, callbackUrl }),
    [clientId, clientScope, issuer, callbackUrl],
  )

  const { asPath, push, query, pathname, replace } = useRouter()

  const logAnalytics = (message: string) => {
    analytics.log({
      message,
    })
  }

  const [activeFlow, setActiveFlow] = useState(
    query.flow ? (query.flow as FlowIdentifier) : ('register' as FlowIdentifier),
  )
  useEffect(() => {
    const { tokens, status } = transaction || {
      tokens: {},
      status: '',
    }

    const proceedOktaFlow = async () => {
      if (!getFromStorage({ storageType: 'local', key: 'standalone_redirect' })) {
        setToStorage({ storageType: 'local', key: 'standalone_redirect', value: redirectUrl })
      }
      try {
        await oktaClient.revokeRefreshToken()
        oktaClient.tokenManager.clear()
        const newTransaction = await oktaClient.idx.proceed()
        setTransaction(newTransaction)
      } catch (error) {
        const catchErrorMessage =
          error instanceof Error ? error.message : (error?.toString() ?? 'Unknown error')
        analytics.logError({
          message: 'Standalone - proceedOktaFlow failed',
          errorMessage: catchErrorMessage,
          redirectUrl,
        })
        if (isWebview) {
          analytics.log({
            message: `webview: proceedOktaFlow failed`,
            hop: 'flow_end',
            elapsedMs: getSamlFlowElapsedMs(),
            correlationId: getSamlFlowCorrelationId(),
          })
          clearSamlFlowTimer()
          window.location.href = getRedirectUrlOnSignInComplete('/', document.baseURI)
        } else {
          void push('/home/join/terminal')
        }
      }
    }

    const startOkta = async () => {
      if (status === IdxStatus.SUCCESS && tokens === undefined) {
        await proceedOktaFlow()
      } else if (status === IdxStatus.SUCCESS) {
        if (isWebview) {
          analytics.log({
            message: `webview: tokens received`,
            hop: 'tokens_received',
            elapsedMs: getSamlFlowElapsedMs(),
            correlationId: getSamlFlowCorrelationId(),
          })
        }
        logSignInSuccess(redirectUrl, activeFlow)

        oktaClient.tokenManager.setTokens(tokens as Tokens)
        if (isWebview) {
          analytics.log({
            message: `webview: tokens set to token manager`,
            hop: 'tokens_set',
            elapsedMs: getSamlFlowElapsedMs(),
            correlationId: getSamlFlowCorrelationId(),
          })
        }
        removeFromStorage({ storageType: 'session', key: 'checkoutURL' })
      } else if (status === IdxStatus.TERMINAL) {
        logAnalytics(`Standalone - switched to Terminal page`)
        setToStorage({
          storageType: 'session',
          key: 'okta_terminal_message',
          value: JSON.stringify(transaction?.messages, null, 4),
        })
        if (isWebview) {
          analytics.log({
            message: `webview: sign in failed. Terminal page`,
            hop: 'flow_end',
            elapsedMs: getSamlFlowElapsedMs(),
            correlationId: getSamlFlowCorrelationId(),
          })
          clearSamlFlowTimer()
          window.location.href = getRedirectUrlOnSignInComplete('/', document.baseURI)
        } else {
          void push('/home/join/terminal')
        }
      } else if (status === IdxStatus.FAILURE) {
        logAnalytics(`Standalone - switched to Error page`)
        if (isWebview) {
          analytics.log({
            message: `webview: sign in failed. Error page`,
            hop: 'flow_end',
            elapsedMs: getSamlFlowElapsedMs(),
            correlationId: getSamlFlowCorrelationId(),
          })
          clearSamlFlowTimer()
          window.location.href = getRedirectUrlOnSignInComplete('/', document.baseURI)
        } else {
          void push('/home/join/error')
        }
      } else if (status === IdxStatus.CANCELED) {
        logAnalytics(`Standalone - switched to Canceled page`)
        void push('/home/join/canceled')
      }
    }

    void startOkta()
  }, [transaction, oktaClient, push, redirectUrl, activeFlow, isWebview])

  useEffect(() => {
    firePageViewEvent(activeFlow)
  }, [activeFlow])

  useEffect(() => {
    const resumeTransaction = async () => {
      try {
        const newTransaction: IdxTransaction = await oktaClient.idx.proceed()
        setTransaction(newTransaction)
      } catch (error) {
        const catchErrorMessage =
          error instanceof Error ? error.message : (error?.toString() ?? 'Unknown error')
        analytics.logError({
          message: `Standalone - resume transaction failed: ${catchErrorMessage}`,
        })
      }
    }

    const checkIsWebview =
      getFromStorage({ storageType: 'session', key: 'isWebview' }) === 'true' ||
      !!new URLSearchParams(window.location.search).get('webview')

    setIsWebview(checkIsWebview)

    if (checkIsWebview) {
      const correlationId = new URLSearchParams(window.location.search).get('correlationId') ?? undefined
      const isFirstRenderStart = startSamlFlowTimer(correlationId)
      if (isFirstRenderStart) {
        analytics.log({
          message: `webview: start okta`,
          hop: 'render_start',
          correlationId: getSamlFlowCorrelationId(),
        })
      }
    }

    if (
      !asPath?.includes('error') &&
      !isRedirectUri(window.location.href, oktaClient) &&
      oktaClient.idx.canProceed()
    ) {
      void resumeTransaction()
    }
  }, [asPath, oktaClient])

  useEffect(() => {
    let component = (
      <FlowPage
        redirectUrl={redirectUrl}
        transaction={transaction}
        setTransaction={setTransaction}
        setSlideInRight={setSlideInRight}
        slideInRight={slideInRight}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        showSpinner={showSpinner}
        setShowSpinner={setShowSpinner}
        flowParameter={activeFlow}
        buttonsDisabled={buttonsDisabled}
        isWebview={isWebview}
        setButtonsDisabled={setButtonsDisabled}
      />
    )
    if (asPath?.includes('terminal')) {
      component = <TerminalPage />
    }

    if (asPath?.includes('error')) {
      component = <ErrorPage />
    }

    if (asPath?.includes('canceled')) {
      component = <CanceledPage />
    }

    if (asPath?.includes('login/callback')) {
      component = <LoginCallback transaction={transaction} setTransaction={setTransaction} />
    }

    setComponentToShow(component)
  }, [
    redirectUrl,
    transaction,
    errorMessage,
    showSpinner,
    activeFlow,
    buttonsDisabled,
    asPath,
    slideInRight,
    oktaClient,
    isWebview,
  ])

  const handleOnClick = (tab: AuthTabType) => {
    setErrorMessage('')
    if (activeFlow !== tab.id) {
      setShowSpinner(true)
    }

    setActiveFlow(tab.id)
    void push(
      {
        pathname,
        query: {
          ...query,
          flow: tab.query.flow,
        },
      },
      undefined,
      { shallow: true },
    )
  }
  const isBackButtonHidden = transaction?.nextStep?.name === 'enroll-authenticator'

  return (
    <Security oktaAuth={oktaClient} restoreOriginalUri={restoreOriginalUri}>
      {isWebview && (
        <WebviewContainer justifyContent='space-around'>
          <div className='relative top-[40%]' data-testid='full-spinner'>
            <Spinner />
          </div>
        </WebviewContainer>
      )}
      {buttonsDisabled && <div className='bg-white absolute z-[4] h-full w-full opacity-50' />}
      <Container size='sm' p={4}>
        <Flex flexDirection='column' justifyContent='space-between' style={{ minHeight: '90vh' }}>
          <Box px={[0, null, 5]}>
            {!isBackButtonHidden && <BackButton onNavigateBack={replace} redirectUrl={redirectUrl} />}
            <Flex alignItems='center' justifyContent='center' mt={[3, null, 4]} mb={24} role='tablist'>
              {AUTH_TABS.map((tab) => {
                const isActive = activeFlow !== undefined ? tab.id === activeFlow : tab.id === 'register'

                return (
                  <Box width={1 / 2} mx={1} key={tab.id}>
                    <ButtonChip
                      disabled={!transaction?.nextStep || showSpinner || buttonsDisabled}
                      id={`tab-${tab.id}`}
                      data-testid={tab.autobotID}
                      aria-selected={isActive}
                      aria-label={tab.label}
                      size='md'
                      role='tab'
                      width='100%'
                      expanded={isActive}
                      onClick={() => {
                        void handleOnClick(tab)
                      }}
                    >
                      <Box px={3} py={1}>
                        <Text textAlign='center' regular fontSize={1}>
                          {tab.tabText}
                        </Text>
                      </Box>
                    </ButtonChip>
                  </Box>
                )
              })}
            </Flex>
            {componentToShow}
            <Box mb='48px' />
          </Box>
          <Box px={[0, null, 5]}>
            <Text textStyle='disclaimer'>
              By signing in or creating an account, you agree with our{' '}
              <Link href='/static-pages/terms_en.html'>Terms & Conditions </Link> and{' '}
              <Link href='/static-pages/privacy-policy.html'>Privacy Statement</Link>.
            </Text>
          </Box>
        </Flex>
      </Container>
    </Security>
  )
}

export default Okta
